import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Camera, Save, ArrowLeft, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ProfileEdit = ({ user, onUserUpdate }) => {
  const [fullName, setFullName] = useState(user.full_name || '');
  const [universidad, setUniversidad] = useState(user.universidad || '');
  const [gender, setGender] = useState(user.gender || '');
  const [imagePreview, setImagePreview] = useState(user.profile_image || null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef(null);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagen muy grande. Máximo 5MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post('/auth/upload-profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImagePreview(res.data.profile_image);
      if (onUserUpdate) onUserUpdate({ ...user, profile_image: res.data.profile_image });
      toast.success('Foto actualizada');
    } catch {
      toast.error('Error subiendo imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!universidad.trim()) { toast.error('La universidad es obligatoria'); return; }
    if (!gender) { toast.error('Selecciona tu sexo'); return; }
    setSaving(true);
    try {
      const res = await apiClient.put('/auth/profile', {
        full_name: fullName.trim(),
        universidad: universidad.trim(),
        gender,
      });
      if (onUserUpdate) onUserUpdate(res.data.user);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-3 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-black text-sm font-medium" data-testid="profile-back-btn">
            <ArrowLeft size={16} />
            <span>Menú</span>
          </Link>
          <h1 className="text-sm font-bold text-black">Mi Perfil</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="pt-16 px-4 max-w-lg mx-auto pb-10">
        {/* Profile Photo */}
        <div className="flex flex-col items-center mt-4 mb-6">
          <div className="relative" data-testid="profile-photo-section">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt={fullName}
                className="w-24 h-24 rounded-full object-cover border-2 border-black"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-black font-bold text-3xl border-2 border-black">
                {fullName.charAt(0).toUpperCase()}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadingImage}
              className="absolute bottom-0 right-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
              data-testid="change-photo-btn"
            >
              {uploadingImage ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Camera size={14} />
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">Toca el ícono para cambiar tu foto</p>
        </div>

        {/* Subscription Info */}
        {user.subscription_expires && (
          <div className="bg-gray-50 rounded-xl p-4 mb-4" data-testid="subscription-info">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} className="text-gray-500" />
              <span className="text-xs font-medium text-gray-500">Suscripción activa</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">
              Vence el {new Date(user.subscription_expires).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {(() => {
              const days = Math.ceil((new Date(user.subscription_expires) - new Date()) / (1000 * 60 * 60 * 24));
              if (days > 0) {
                return <p className="text-xs text-gray-400 mt-0.5">{days} días restantes</p>;
              }
              return <p className="text-xs text-red-500 mt-0.5 font-medium">Suscripción expirada</p>;
            })()}
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Correo electrónico</label>
            <input
              type="email"
              value={user.email || ''}
              disabled
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-400"
              data-testid="profile-email"
            />
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nombre completo</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              data-testid="profile-name"
            />
          </div>

          {/* Universidad */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Universidad</label>
            <input
              type="text"
              value={universidad}
              onChange={e => setUniversidad(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              data-testid="profile-universidad"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sexo</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'male', label: 'Masculino' },
                { value: 'female', label: 'Femenino' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setGender(opt.value)}
                  className={`border rounded-xl py-3 text-sm font-medium text-center transition-all ${
                    gender === opt.value
                      ? 'border-black bg-black text-white'
                      : 'border-gray-300 hover:border-gray-500'
                  }`}
                  data-testid={`profile-gender-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-black text-white rounded-xl py-3.5 font-semibold text-sm disabled:opacity-40 hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mt-6"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save size={16} />
                <span>Guardar cambios</span>
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
};

export default ProfileEdit;
